import { useEffect, useState } from 'react';
import { api, zeitpunkt } from '../api.ts';
import { Auswahl, Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage } from './Formular.tsx';
import { useFrisch } from './Frisch.tsx';

/**
 * EINEN AUFTRAG FÜR EINE PARTNERFIRMA FREIGEBEN UND ENTZIEHEN (.278).
 *
 * ORIGINALTEXT MARVIN (02.10.2026)
 *   „wenn ich jemandem den auftrag "freigeben und entziehen" will, so dass er
 *    ihn sehen kann, ohne einen termin dafür buchen zu müssen, zb. wenn dort
 *    kurzfristig ein anderes team hinfahren muss."
 */
export function PartnerfreigabeDialog({ auftragId, baustelle, zu }: { auftragId: string; baustelle?: string; zu: () => void }) {
  const [d, setD] = useState<any>(null);
  const [firmen, setFirmen] = useState<any[]>([]);
  const [firma, setFirma] = useState('');
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  async function laden() {
    try { setD(await api.partnerfreigaben(auftragId)); } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, [auftragId]);
  useFrisch(laden);
  useEffect(() => { api.subunternehmer().then((l: any[]) => setFirmen(l.filter((x) => x.aktiv !== false))).catch(() => setFirmen([])); }, []);

  const offen = (d?.freigaben ?? []).filter((f: any) => !f.entzogen_am);
  const vorbei = (d?.freigaben ?? []).filter((f: any) => f.entzogen_am);
  const schon = new Set<string>([...offen.map((f: any) => f.subunternehmer_id), ...(d?.ueber_gewerk ?? []).map((g: any) => g.id)]);

  async function freigeben() {
    setLaeuft(true); setFehler(null); setHinweis(null);
    try {
      const r = await api.partnerfreigeben(auftragId, { subunternehmer_id: firma, grund });
      setHinweis(r.klartext); setFirma(''); setGrund(''); await laden();
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }
  async function entziehen(f: any) {
    setLaeuft(true); setFehler(null); setHinweis(null);
    try { const r = await api.partnerentziehen(auftragId, f.id); setHinweis(r.klartext); await laden(); }
    catch (x) { setFehler(ausFehler(x)); } finally { setLaeuft(false); }
  }

  return (
    <Dialog titel="Für Partner freigeben" untertitel={baustelle} breite={620} laeuft={laeuft} zu={zu}>
      <Fehlerkasten lage={fehler} />
      {hinweis && <div className="hinweis erledigt">{hinweis}</div>}
      <p className="klein leise" style={{ marginTop: 0 }}>
        Die Firma sieht den Auftrag wie einen eigenen und kann Dateien hochladen, ohne dass ein Termin gebucht wird.
        Der Termin der anderen Firma bleibt, wie er ist.
      </p>
      {!!d?.ueber_gewerk?.length && (
        <p className="klein">Sehen ihn schon über ein Gewerk: <b>{d.ueber_gewerk.map((g: any) => g.firma).join(', ')}</b></p>
      )}
      <h3 style={{ margin: '10px 0 6px' }}>Freigegeben</h3>
      {!offen.length && <p className="leise klein">Keine Firma.</p>}
      {offen.map((f: any) => (
        <div key={f.id} className="reihe" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
          <b>{f.firma}</b>
          <span className="klein leise">{zeitpunkt(f.freigegeben_am)}{f.freigegeben_von ? `, ${f.freigegeben_von}` : ''}{f.grund ? `, ${f.grund}` : ''}</span>
          <div className="wachsen" />
          <button className="knopf klein" disabled={laeuft} onClick={() => entziehen(f)}>entziehen</button>
        </div>
      ))}
      <h3 style={{ margin: '14px 0 6px' }}>Neue Freigabe</h3>
      <div className="feldreihe">
        <Auswahl name="subunternehmer_id" titel="Firma" wert={firma} setze={setFirma} breite={280} fehler={fehler}
                 leerText="bitte wählen"
                 optionen={firmen.filter((x) => !schon.has(x.id)).map((x) => ({ wert: x.id, text: x.firma }))} />
        <Text name="grund" titel="Grund (freiwillig)" wert={grund} setze={setGrund} breite={260} fehler={fehler}
              hinweis="Zum Beispiel: fährt kurzfristig statt der eingeplanten Firma." />
      </div>
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt" disabled={laeuft || !firma} onClick={freigeben}>Freigeben</button>
      </div>
      {!!vorbei.length && (
        <details style={{ marginTop: 12 }}>
          <summary className="klein">Früher freigegeben ({vorbei.length})</summary>
          {vorbei.map((f: any) => (
            <div key={f.id} className="klein leise">{f.firma}: {zeitpunkt(f.freigegeben_am)} bis {zeitpunkt(f.entzogen_am)}{f.entzogen_von ? ` (${f.entzogen_von})` : ''}</div>
          ))}
        </details>
      )}
    </Dialog>
  );
}
